import { applySnapshot, flow, getSnapshot, types } from "mobx-state-tree";
import { ACTIVE_PROGRAM_TYPE } from "constants/models";
import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";
import api from "services/api";
import { MSURL } from "utils/ms";
import { isEmptyObject } from "utils";
import { FILTER_COLUMNS } from "constants/filterConstants";
import { Claim, ClaimDetail } from "./ClaimStore";

const corePath = "/core/api/v1";

export const ActiveProgramModel = types
  .model({
    name: types.string,
    value: types.string,
    id: types.number,
    workoutSequenceId: types.maybeNull(types.number),
    activeProgramType: types.enumeration(Object.values(ACTIVE_PROGRAM_TYPE)),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
      };
    },
  }));

const ActiveProgramStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`${corePath}/active-program`, params, undefined, MSURL);
  }),
  initialOrder: { column: "Created", desc: true },
  itemModel: ActiveProgramModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(ActiveProgramModel),
    isProgramLoading: types.optional(types.boolean, false),
    isProgramManaging: types.optional(types.boolean, false),
    isProgramPublishing: types.optional(types.boolean, false),
    isProgramCopying: types.optional(types.boolean, false),
    isManaging: types.optional(types.boolean, false),
    isMoreLoading: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },
  }))
  .actions(self => ({
    updateActiveProgram(id: number, data: any) {
      const { items } = getSnapshot(self);

      const filtersContainNewStatus =
        isEmptyObject(self.filters) ||
        self.filters[FILTER_COLUMNS.ACTIVE_PROGRAM_TYPE]?.includes(
          data.activeProgramType,
        );

      if (!filtersContainNewStatus) {
        const filteredItems = self.items.filter(item => item.id !== id);
        applySnapshot(self, { ...self, items: filteredItems });
      } else {
        const index = self.items.findIndex(item => item.id === id);
        self.items[index] = { ...items[index], ...data };
      }
    },
  }))
  .actions(self => ({
    fetchMore: flow(function*() {
      self.isProgramLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);
          const { items } = yield api.get(
            `${corePath}/active-program`,
            {
              size: DEFAULT_PAGE_SIZE,
              page: self.page,
              ...self.filters,
            },
            undefined,
            MSURL,
          );

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isProgramLoading = false;
      }
    }),

    fetchDetails: flow(function*(id?: number) {
      self.isProgramLoading = true;

      try {
        const details = yield api.get(
          `${corePath}/active-program/${id}`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isProgramLoading = false;
      }
    }),

    createItem: flow(function*(payload) {
      self.isManaging = true;

      try {
        const { id } = yield api.post(
          `${corePath}/active-program`,
          payload,
          undefined,
          MSURL,
        );
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isManaging = false;
      }
    }),

    editItem: flow(function*(payload: any) {
      self.isManaging = true;

      try {
        return yield api.put(
          `${corePath}/active-program/${payload.id}`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isManaging = false;
      }
    }),

    resetDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },
  }));

export default ActiveProgramStore.create();
